import {
  LLMFeedbackResponse,
  LLMFeedbackParams,
  LLMFeedbackCreateResponse,
} from '@ltvco/refresh-lib/api';
import {
  Button,
  IconButton,
  Modal,
  Stack,
  Text,
  TextArea,
  useTheme,
} from '@ltvco/refresh-lib/theme';
import { useScreenSize, ResponseError } from '@ltvco/refresh-lib/utils';
import { UseMutationResult } from '@ltvco/refresh-lib/vendors';
import CloseIcon from '@mui/icons-material/Close';
import { useState, useEffect } from 'react';

type ChatFeedbackModalProps = {
  open: boolean;
  onClose: () => void;
  thumbsUp: boolean;
  response_item_id: string;
  createFeedback: UseMutationResult<
    LLMFeedbackCreateResponse,
    ResponseError,
    LLMFeedbackParams
  >;
  updateFeedback: UseMutationResult<
    LLMFeedbackResponse,
    ResponseError,
    LLMFeedbackParams
  >;
  hasRecord: boolean;
  feedbackText: string;
};

export const ChatFeedbackModal = ({
  open,
  onClose,
  thumbsUp,
  response_item_id,
  createFeedback,
  updateFeedback,
  hasRecord,
  feedbackText,
}: ChatFeedbackModalProps) => {
  const { isMobile } = useScreenSize();
  const theme = useTheme();
  const [currentFeedbackText, setCurrentFeedbackText] = useState(feedbackText);

  const handleSendFeedback = () => {
    const feedbackParams: LLMFeedbackParams = {
      response_item_id,
      thumbs_up: thumbsUp,
      feedback_text: currentFeedbackText,
    };

    if (hasRecord) {
      updateFeedback.mutate(feedbackParams);
    } else {
      createFeedback.mutate(feedbackParams);
    }
    setCurrentFeedbackText('');
    onClose();
  };

  useEffect(() => {
    if (open && feedbackText) {
      setCurrentFeedbackText(feedbackText);
    } else {
      setCurrentFeedbackText('');
    }
  }, [feedbackText, open]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiDialogContent-root': {
          padding: 0,
        },
        '& .MuiDialog-paper': {
          borderRadius: theme.spacing(2),
        },
      }}
    >
      <Stack
        sx={{ width: isMobile ? '100%' : '400px', padding: 0 }}
        spacing={2}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{
            color: theme.palette.text.contrastText,
            backgroundColor: theme.palette.primary.main,
            height: '56px',
            px: 2,
          }}
        >
          <Text variant="h4" sx={{ fontWeight: 'bold', mb: 0 }}>
            Feedback
          </Text>
          <IconButton onClick={onClose}>
            <CloseIcon sx={{ color: theme.palette.text.contrastText }} />
          </IconButton>
        </Stack>
        <Stack sx={{ px: 2 }}>
          <Text variant="h5">Your feedback</Text>
          <TextArea
            placeholder="Write your feedback here..."
            value={currentFeedbackText}
            onChange={(e) => setCurrentFeedbackText(e.target.value)}
          />
        </Stack>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ padding: 2 }}
        >
          <Button
            variant="outlined"
            sx={{
              border: 'none',
              color: theme.palette.text.secondary,
              '&:hover': {
                border: 'none',
                backgroundColor: 'transparent',
              },
            }}
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            sx={{
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.text.contrastText,
            }}
            onClick={() => handleSendFeedback()}
          >
            Send
          </Button>
        </Stack>
      </Stack>
    </Modal>
  );
};
